import * as amplitude from '@amplitude/analytics-browser'
import { AddonTypeEnum, SubscriptionProductType } from '~/enums'
import { ChallengeAddon } from '~/types/challenge'
import { Offer, OfferAddon, TechnologyPlatform } from '~/types/offer'
import type { User } from '~/services/auth.server'
import { CurrencyData } from '~/types/api-client'

export function onPathnameChanged(args: { pathname?: string; user?: User | null; locale?: string }) {
  window.dataLayer?.push({
    event: 'custom_page_view',
    event_name: 'page_view',
    login_status: Boolean(args.user).toString(),
    language: args.locale ?? undefined,
    page_environment: window.ENV.ENV_NAME === 'production' ? 'prod' : 'dev',
    page_type: args.pathname?.startsWith('/affiliate') ? 'affiliate' : 'client_area',
    content_type: undefined,
    content_topic: undefined,
    user_id: args.user?.clientId,
    user_type: undefined, // [user_type], // TODO:
    is_affiliated: undefined, // [is_affiliated], // TODO:
  })
}

export function onSignupFirstNameFilled(args: { firstName: string }) {
  amplitude.track('Name Filled')
}

export function onSignupLastNameFilled(args: { lastName: string }) {
  amplitude.track('Last Name Filled')
}

export function onSignupEmailFilled(args: { email: string }) {
  amplitude.track('Emailed Filled')
}

export function onSignupCountryChosen(args: { countryName: string }) {
  amplitude.track('Country Chosen', {
    'сountry name': args.countryName,
  })
}

export function onSignupPhoneFilled(args: { phone: string }) {
  amplitude.track('Phone Filled')
}

export function onSignupTermsAccepted() {
  amplitude.track('Terms Accepted')
}

export function onSignupNewsletterSubscribed() {
  amplitude.track('Newsletter Wanted')
}

export function onSignupRecaptchaAttempted(args: { passed: boolean }) {
  amplitude.track('Recaptcha Passed', {
    'recaptcha passed': args.passed ? 'cliked passed' : 'cliked failed',
  })
}

export function onQuickCheckoutCfdSelected(args: {
  offer: Offer & { evaluationType: string }
  platform: TechnologyPlatform | null | undefined
  fiatCurrency: CurrencyData
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'select_item',
    funnel_type: 'quick_checkout',
    funnel_step: 2,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.offer.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
          item_variant: undefined,
        },
      ],
    },
  })
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_to_cart',
    funnel_type: 'quick_checkout',
    funnel_step: 3,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.offer.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
        },
      ],
    },
  })
}

export function onQuickCheckoutFuturesCardSelected(args: {
  offer: Offer & { evaluationType: string }
  fiatCurrency: CurrencyData
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'select_item',
    funnel_type: 'quick_checkout',
    funnel_step: 2,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.offer.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: undefined,
          price: args.offer.price,
          item_variant: undefined,
        },
      ],
    },
  })
}

export function onQuickCheckoutFuturesSelected(args: {
  offer: Offer & { evaluationType: string }
  platform: Omit<TechnologyPlatform, 'enabled'> | null | undefined
  addons?: ChallengeAddon[]
  fiatCurrency: CurrencyData
}) {
  // window.dataLayer?.push({
  //   event: 'ga4ecommerce',
  //   event_name: 'select_item',
  //   funnel_type: 'quick_checkout',
  //   funnel_step: 2,
  //   ecommerce: {
  //     currency: args.fiatCurrency.code,
  //     items: [
  //       {
  //         item_id: args.offer.offerId,
  //         item_name: args.offer.displayName,
  //         coupon: undefined,
  //         discount: undefined,
  //         item_category: args.offer.assetGroup,
  //         item_category2: 'new_challenge',
  //         item_category3: args.offer.accountBalance,
  //         item_category4: args.offer.evaluationType,
  //         item_category5: args.platform?.name,
  //         price: args.offer.price,
  //         item_variant: args.addons?.map((a) => a.displayName).join(', '),
  //       },
  //     ],
  //   },
  // })
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_to_cart',
    funnel_type: 'quick_checkout',
    funnel_step: 3,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.offer.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
          item_variant: args.addons?.map((a) => a.displayName).join(', '),
        },
      ],
    },
  })
  const futuresTradingPlatform = args.platform
    ? {
        NinjaTrader: 'ninjatrader',
        Tradovate: 'tradovate',
        ATAS: 'atas',
        Quantower: 'quantower',
        Deepcharts: 'deepcharts',
        'R-Trader Pro': 'rtrader',
      }[args.platform.name]
    : undefined
  const futuresAddedDatesCode = args.addons?.find((a) => a.code === 'FUDAY60' || a.code === 'FUDAY90')?.code
  const futuresMarketDataAddon = args.addons
    ?.filter((a) => a.type === AddonTypeEnum.MARKETDATA)
    .map((a) => {
      return {
        [SubscriptionProductType.L1BUNDLE]: 'level 1 us',
        [SubscriptionProductType.L2CME]: 'level 2 chicago',
        [SubscriptionProductType.L2]: 'level 2 us',
        [SubscriptionProductType.EUREX]: 'european market data',
      }[a.code]
    })
    .filter(Boolean)
    .join(', ')

  if (futuresTradingPlatform) {
    amplitude.track('Futures Platform, Addons Chosen', {
      'futures trading platform': futuresTradingPlatform,
      'futures market data addon': futuresMarketDataAddon,
      'futures added dates': futuresAddedDatesCode
        ? { FUDAY60: '60', FUDAY90: '90' }[futuresAddedDatesCode]
        : undefined,
    })
  }
}

export function onQuickCheckoutStocksCardSelected(args: {
  offer: Offer & { evaluationType: string }
  platform: Omit<TechnologyPlatform, 'enabled'> | null | undefined
  fiatCurrency: CurrencyData
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'select_item',
    funnel_type: 'quick_checkout',
    funnel_step: 2,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: 'stocks',
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
          item_variant: undefined,
        },
      ],
    },
  })
}

export function onQuickCheckoutStocksSelected(args: {
  offer: Offer & { evaluationType: string }
  platform: Omit<TechnologyPlatform, 'enabled'> | null | undefined
  addons?: OfferAddon[]
  fiatCurrency: CurrencyData
}) {
  // window.dataLayer?.push({
  //   event: 'ga4ecommerce',
  //   event_name: 'select_item',
  //   funnel_type: 'quick_checkout',
  //   funnel_step: 2,
  //   ecommerce: {
  //     currency: args.fiatCurrency.code,
  //     items: [
  //       {
  //         item_id: args.offer.offerId,
  //         item_name: args.offer.displayName,
  //         coupon: undefined,
  //         discount: undefined,
  //         item_category: 'stocks',
  //         item_category2: 'new_challenge',
  //         item_category3: args.offer.accountBalance,
  //         item_category4: args.offer.evaluationType,
  //         item_category5: args.platform?.name,
  //         price: args.offer.price,
  //         item_variant: args.addons?.map((a) => a.displayName).join(', '),
  //       },
  //     ],
  //   },
  // })
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_to_cart',
    funnel_type: 'quick_checkout',
    funnel_step: 3,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: 'stocks',
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
          item_variant: args.addons?.map((a) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onQuickCheckoutBackToProductsFromPersonalInfoClicked(args: { challengeData: any }) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'remove_from_cart',
    funnel_type: 'quick_checkout',
    ecommerce: {
      currency: args.challengeData?.fiatCurrency?.code,
      items: [
        {
          item_id: args.challengeData?.selectedOffer?.offerId,
          item_name: args.challengeData?.selectedOffer?.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.challengeData?.selectedOffer?.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.challengeData?.selectedOffer?.accountBalance,
          item_category4: args.challengeData?.selectedOffer?.evaluationType,
          item_category5:
            args.challengeData?.marketDataPlatform?.name ??
            args.challengeData?.selectedOffer?.technologyPlatforms?.find(
              (p: any) => p.id === args.challengeData?.cfdSelectedPlatform,
            )?.name,
          price: args.challengeData?.selectedOffer?.price,
          item_variant: args.challengeData?.selectedAddons.map((a: any) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onQuickCheckoutPersonalInformationPageEntered(args: { challengeData: any }) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'begin_checkout',
    funnel_type: 'quick_checkout',
    funnel_step: 4,
    ecommerce: {
      currency: args.challengeData?.fiatCurrency?.code,
      items: [
        {
          item_id: args.challengeData?.selectedOffer?.offerId,
          item_name: args.challengeData?.selectedOffer?.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.challengeData?.selectedOffer?.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.challengeData?.selectedOffer?.accountBalance,
          item_category4: args.challengeData?.selectedOffer?.evaluationType,
          item_category5:
            args.challengeData?.marketDataPlatform?.name ??
            args.challengeData?.selectedOffer?.technologyPlatforms?.find(
              (p: any) => p.id === args.challengeData?.cfdSelectedPlatform,
            )?.name,
          price: args.challengeData?.selectedOffer?.price,
          item_variant: args.challengeData?.selectedAddons.map((a: any) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onQuickCheckoutPersonalInformationNextButtonClicked(args: { challengeData: any }) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_shipping_info',
    funnel_type: 'quick_checkout',
    funnel_step: 5,
    ecommerce: {
      currency: args.challengeData?.fiatCurrency?.code,
      items: [
        {
          item_id: args.challengeData?.selectedOffer?.offerId,
          item_name: args.challengeData?.selectedOffer?.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.challengeData?.selectedOffer?.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.challengeData?.selectedOffer?.accountBalance,
          item_category4: args.challengeData?.selectedOffer?.evaluationType,
          item_category5:
            args.challengeData?.marketDataPlatform?.name ??
            args.challengeData?.selectedOffer?.technologyPlatforms?.find(
              (p: any) => p.id === args.challengeData?.cfdSelectedPlatform,
            )?.name,
          price: args.challengeData?.selectedOffer?.price,
          item_variant: args.challengeData?.selectedAddons.map((a: any) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onQuickCheckoutCheckoutRequest(args: { challengeData: any }) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_payment_info',
    funnel_type: 'quick_checkout',
    funnel_step: 6,
    ecommerce: {
      currency: args.challengeData?.fiatCurrency?.code,
      items: [
        {
          item_id: args.challengeData?.selectedOffer?.offerId,
          item_name: args.challengeData?.selectedOffer?.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.challengeData?.selectedOffer?.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.challengeData?.selectedOffer?.accountBalance,
          item_category4: args.challengeData?.selectedOffer?.evaluationType,
          item_category5:
            args.challengeData?.marketDataPlatform?.name ??
            args.challengeData?.selectedOffer?.technologyPlatforms?.find(
              (p: any) => p.id === args.challengeData?.cfdSelectedPlatform,
            )?.name,
          price: args.challengeData?.selectedOffer?.price,
          item_variant: args.challengeData?.selectedAddons.map((a: any) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onPaymentResultPageEntered(args: {
  challengeData: any
  paymentResult: 'SUCCESS' | 'FAILED' | 'CANCELED' | 'PROCESSING'
  paymentId: string
  storedCheckoutData: any
}) {
  if (args.paymentResult === 'SUCCESS' && args.storedCheckoutData?.url?.includes('quick-checkout')) {
    window.dataLayer?.push({
      event: 'ga4ecommerce',
      event_name: 'purchase',
      funnel_type: 'quick_checkout',
      funnel_step: 7,
      ecommerce: {
        transaction_id: args.paymentId,
        currency: args.challengeData?.fiatCurrency?.code,
        items: [
          {
            item_id: args.challengeData?.selectedOffer?.offerId,
            item_name: args.challengeData?.selectedOffer?.displayName,
            coupon: undefined,
            discount: undefined,
            item_category: args.challengeData?.selectedOffer?.assetGroup,
            item_category2: 'new_challenge',
            item_category3: args.challengeData?.selectedOffer?.accountBalance,
            item_category4: args.challengeData?.selectedOffer?.evaluationType,
            item_category5:
              args.challengeData?.marketDataPlatform?.name ??
              args.challengeData?.selectedOffer?.technologyPlatforms?.find(
                (p: any) => p.id === args.challengeData?.cfdSelectedPlatform,
              )?.name,
            price: args.challengeData?.selectedOffer?.price,
            item_variant: args.challengeData?.selectedAddons.map((a: any) => a.displayName).join(', '),
          },
        ],
      },
    })
  } else if (args.paymentResult === 'SUCCESS') {
    window.dataLayer?.push({
      event: 'ga4ecommerce',
      event_name: 'purchase',
      funnel_type: 'logged_checkout',
      funnel_step: 6,
      ecommerce: {
        transaction_id: args.paymentId,
        currency: args.challengeData?.fiatCurrency?.code,
        items: [
          {
            item_id: args.challengeData?.selectedOffer?.offerId,
            item_name: args.challengeData?.selectedOffer?.displayName,
            coupon: undefined,
            discount: undefined,
            item_category: args.challengeData?.selectedOffer?.assetGroup,
            item_category2: 'new_challenge',
            item_category3: args.challengeData?.selectedOffer?.accountBalance,
            item_category4: args.challengeData?.selectedOffer?.evaluationType,
            item_category5:
              args.challengeData?.marketDataPlatform?.name ??
              args.challengeData?.selectedOffer?.technologyPlatforms?.find(
                (p: any) => p.id === args.challengeData?.cfdSelectedPlatform,
              )?.name,
            price: args.challengeData?.selectedOffer?.price,
            item_variant: args.challengeData?.selectedAddons.map((a: any) => a.displayName).join(', '),
          },
        ],
      },
    })
  }
}

export function onStartChallengeClicked(args: {
  title: string
  url: string
  location: 'side_menu' | 'accounts' | 'home'
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'begin_logged_checkout',
    link_text: args.title,
    link_url: args.url,
    location: args.location,
    funnel_type: 'logged_checkout',
    funnel_step: 1,
  })
}

export function onChallengesCfdSelected(args: {
  offer: Offer & { evaluationType: string }
  platform: TechnologyPlatform | null | undefined
  fiatCurrency: CurrencyData
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'select_item',
    funnel_type: 'logged_checkout',
    funnel_step: 2,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.offer.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
          item_variant: undefined,
        },
      ],
    },
  })
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_to_cart',
    funnel_type: 'logged_checkout',
    funnel_step: 3,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.offer.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
        },
      ],
    },
  })
}

export function onChallengesFuturesCardSelected(args: {
  offer: Offer & { evaluationType: string }
  fiatCurrency: CurrencyData
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'select_item',
    funnel_type: 'logged_checkout',
    funnel_step: 2,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.offer.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: undefined,
          price: args.offer.price,
          item_variant: undefined,
        },
      ],
    },
  })
}

export function onChallengesFuturesSelected(args: {
  offer: Offer & { evaluationType: string }
  platform: Omit<TechnologyPlatform, 'enabled'> | null | undefined
  addons?: ChallengeAddon[]
  fiatCurrency: CurrencyData
}) {
  // window.dataLayer?.push({
  //   event: 'ga4ecommerce',
  //   event_name: 'select_item',
  //   funnel_type: 'logged_checkout',
  //   funnel_step: 2,
  //   ecommerce: {
  //     currency: args.fiatCurrency.code,
  //     items: [
  //       {
  //         item_id: args.offer.offerId,
  //         item_name: args.offer.displayName,
  //         coupon: undefined,
  //         discount: undefined,
  //         item_category: args.offer.assetGroup,
  //         item_category2: 'new_challenge',
  //         item_category3: args.offer.accountBalance,
  //         item_category4: args.offer.evaluationType,
  //         item_category5: args.platform?.name,
  //         price: args.offer.price,
  //         item_variant: args.addons?.map((a) => a.displayName).join(', '),
  //       },
  //     ],
  //   },
  // })
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_to_cart',
    funnel_type: 'logged_checkout',
    funnel_step: 3,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.offer.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
          item_variant: args.addons?.map((a) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onChallengesStocksCardSelected(args: {
  offer: Offer & { evaluationType: string }
  platform: Omit<TechnologyPlatform, 'enabled'> | null | undefined
  fiatCurrency: CurrencyData
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'select_item',
    funnel_type: 'logged_checkout',
    funnel_step: 2,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: 'stocks',
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
          item_variant: undefined,
        },
      ],
    },
  })
}

export function onChallengesStocksSelected(args: {
  offer: Offer & { evaluationType: string }
  platform: Omit<TechnologyPlatform, 'enabled'> | null | undefined
  addons?: OfferAddon[]
  fiatCurrency: CurrencyData
}) {
  // window.dataLayer?.push({
  //   event: 'ga4ecommerce',
  //   event_name: 'select_item',
  //   funnel_type: 'logged_checkout',
  //   funnel_step: 2,
  //   ecommerce: {
  //     currency: args.fiatCurrency.code,
  //     items: [
  //       {
  //         item_id: args.offer.offerId,
  //         item_name: args.offer.displayName,
  //         coupon: undefined,
  //         discount: undefined,
  //         item_category: 'stocks',
  //         item_category2: 'new_challenge',
  //         item_category3: args.offer.accountBalance,
  //         item_category4: args.offer.evaluationType,
  //         item_category5: args.platform?.name,
  //         price: args.offer.price,
  //         item_variant: args.addons?.map((a) => a.displayName).join(', '),
  //       },
  //     ],
  //   },
  // })
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_to_cart',
    funnel_type: 'logged_checkout',
    funnel_step: 3,
    ecommerce: {
      currency: args.fiatCurrency.code,
      items: [
        {
          item_id: args.offer.offerId,
          item_name: args.offer.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: 'stocks',
          item_category2: 'new_challenge',
          item_category3: args.offer.accountBalance,
          item_category4: args.offer.evaluationType,
          item_category5: args.platform?.name,
          price: args.offer.price,
          item_variant: args.addons?.map((a) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onChallengesBackToProductsFromPaymentClicked(args: { challengeData: any }) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'remove_from_cart',
    funnel_type: 'logged_checkout',
    ecommerce: {
      currency: args.challengeData?.fiatCurrency?.code,
      items: [
        {
          item_id: args.challengeData?.selectedOffer?.offerId,
          item_name: args.challengeData?.selectedOffer?.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.challengeData?.selectedOffer?.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.challengeData?.selectedOffer?.accountBalance,
          item_category4: args.challengeData?.selectedOffer?.evaluationType,
          item_category5:
            args.challengeData?.marketDataPlatform?.name ??
            args.challengeData?.selectedOffer?.technologyPlatforms?.find(
              (p: any) => p.id === args.challengeData?.cfdSelectedPlatform,
            )?.name,
          price: args.challengeData?.selectedOffer?.price,
          item_variant: args.challengeData?.selectedAddons.map((a: any) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onChallengesPaymentPageEntered(args: { challengeData: any }) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'begin_checkout',
    funnel_type: 'logged_checkout',
    funnel_step: 4,
    ecommerce: {
      currency: args.challengeData?.fiatCurrency?.code,
      items: [
        {
          item_id: args.challengeData?.selectedOffer?.offerId,
          item_name: args.challengeData?.selectedOffer?.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.challengeData?.selectedOffer?.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.challengeData?.selectedOffer?.accountBalance,
          item_category4: args.challengeData?.selectedOffer?.evaluationType,
          item_category5:
            args.challengeData?.marketDataPlatform?.name ??
            args.challengeData?.selectedOffer?.technologyPlatforms?.find(
              (p: any) => p.id === args.challengeData?.cfdSelectedPlatform,
            )?.name,
          price: args.challengeData?.selectedOffer?.price,
          item_variant: args.challengeData?.selectedAddons.map((a: any) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onChallengesCheckoutRequest(args: { challengeData: any }) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_payment_info',
    funnel_type: 'logged_checkout',
    funnel_step: 5,
    ecommerce: {
      currency: args.challengeData?.fiatCurrency?.code,
      items: [
        {
          item_id: args.challengeData?.selectedOffer?.offerId,
          item_name: args.challengeData?.selectedOffer?.displayName,
          coupon: undefined,
          discount: undefined,
          item_category: args.challengeData?.selectedOffer?.assetGroup,
          item_category2: 'new_challenge',
          item_category3: args.challengeData?.selectedOffer?.accountBalance,
          item_category4: args.challengeData?.selectedOffer?.evaluationType,
          item_category5:
            args.challengeData?.marketDataPlatform?.name ??
            args.challengeData?.selectedOffer?.technologyPlatforms?.find(
              (p: any) => p.id === args.challengeData?.cfdSelectedPlatform,
            )?.name,
          price: args.challengeData?.selectedOffer?.price,
          item_variant: args.challengeData?.selectedAddons.map((a: any) => a.displayName).join(', '),
        },
      ],
    },
  })
}

export function onMenuItemClicked(args: {
  title: string
  url: string
  location: 'side' | 'footer' | 'top_right'
  outbound?: boolean
}) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'click_menu',
    link_text: args.title,
    link_url: args.url,
    location: args.location,
  })
  if (args.outbound) {
    window.dataLayer?.push({
      event: 'custom_event',
      event_name: 'click_outbound_link',
      link_text: args.title,
      link_url: args.url,
      location: args.location,
    })
  }
}

export function onDropdownMenuClicked(args: {
  title: string
  url?: string
  location: 'header' | 'hero_banner' | 'body' | 'footer'
}) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'click_dropdown',
    link_text: args.title,
    location: args.location,
    link_url: args.url,
  })
}

export function onDownloadClicked(args: {
  title?: string
  location: 'header' | 'hero_banner' | 'body' | 'footer'
  url?: string
  fileName?: string
  fileExtension?: string
}) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'click_download',
    link_text: args.title,
    location: args.location,
    link_url: args.url,
    file_name: args.fileName,
    file_extension: args.fileExtension,
  })
}

export function onPopupOpened(args: { title?: string; location: 'menu' | 'body' | 'footer'; url?: string }) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'open_popup',
    link_text: args.title,
    location: args.location,
    link_url: args.url,
  })
}

export function onPopupInteracted(args: {
  title?: 'close_popup' | (string & {})
  location: 'menu' | 'body' | 'footer'
  url?: string
}) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'popup_interaction',
    link_text: args.title,
    location: args.location,
    link_url: args.url,
  })
}

export function onFormStart(args: {
  title?: string
  formType: 'ebook' | 'webinar' | 'login' | 'signup' | 'affiliation' | 'other'
  location: 'header' | 'checkout' | 'quick_checkout'
}) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'form_start',
    link_text: args.title,
    form_type: args.formType,
    location: args.location,
  })
}

export function onFormSubmit(args: {
  title?: string
  formType: 'ebook' | 'webinar' | 'login' | 'signup' | 'affiliation' | 'other' | 'forgot_password'
  location: 'header' | 'checkout' | 'signup' | 'body'
  email: string
}) {
  let crypto = require('crypto')
  let hashedEmail = crypto.createHash('sha256').update(args.email).digest('hex')
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'form_submit',
    link_text: args.title,
    form_type: args.formType,
    location: args.location,
    hashed_email: hashedEmail,
  })
}

export function onFormConfirmation(args: {
  title?: string
  formType: 'ebook' | 'webinar' | 'login' | 'signup' | 'affiliation' | 'other'
  location: 'header' | 'checkout' | 'signup'
  email: string
}) {
  let crypto = require('crypto')
  let hashedEmail = crypto.createHash('sha256').update(args.email).digest('hex')
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'form_confirmation',
    link_text: args.title,
    form_type: args.formType,
    location: args.location,
    hashed_email: hashedEmail,
  })
}

export function onFormError(args: {
  title?: string
  formType: 'ebook' | 'webinar' | 'login' | 'signup' | 'affiliation' | 'other'
  location: 'header' | 'checkout' | 'signup'
  errorMessage?: string
}) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'form_error',
    link_text: args.title,
    form_type: args.formType,
    location: args.location,
    error_message: args.errorMessage,
  })
}

export function onAccountsResetClicked(args: {
  linkText: string
  destinationUrl: string
  location: 'side_menu' | 'accounts'
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'begin_reset_checkout',
    link_text: args.linkText,
    link_url: args.destinationUrl,
    location: args.location,
    funnel_type: 'reset_checkout',
    funnel_step: 1,
  })
}

export function onResetCheckoutClicked(args: {
  offerId?: string
  displayName?: string
  totalPrice?: number
  voucherCode?: string
  voucherDiscount?: number
  currency?: string
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_payment_info',
    funnel_type: 'reset_checkout',
    funnel_step: 2,
    ecommerce: {
      currency: args.currency,
      items: [
        {
          item_id: args.offerId,
          item_name: args.displayName,
          coupon: args.voucherCode,
          discount: args.voucherDiscount,
          item_category: undefined,
          item_category2: undefined,
          item_category3: undefined,
          item_category4: undefined,
          item_category5: undefined,
          price: args.totalPrice,
          item_variant: undefined,
        },
      ],
    },
  })
}

export function onAccountsExtendClicked(args: {
  linkText: string
  destinationUrl: string
  location: 'side_menu' | 'accounts'
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'begin_extent_checkout',
    link_text: args.linkText,
    link_url: args.destinationUrl,
    location: args.location,
    funnel_type: 'extend_checkout',
    funnel_step: 1,
  })
}

export function onExtendCheckoutClicked(args: {
  offerId?: string
  displayName?: string
  totalPrice?: number
  voucherCode?: string
  voucherDiscount?: number
  currency?: string
}) {
  window.dataLayer?.push({
    event: 'ga4ecommerce',
    event_name: 'add_payment_info',
    funnel_type: 'extent_checkout',
    funnel_step: 2,
    ecommerce: {
      currency: args.currency,
      items: [
        {
          item_id: args.offerId,
          item_name: args.displayName,
          coupon: args.voucherCode,
          discount: args.voucherDiscount,
          item_category: undefined,
          item_category2: undefined,
          item_category3: undefined,
          item_category4: undefined,
          item_category5: undefined,
          price: args.totalPrice,
          item_variant: undefined,
        },
      ],
    },
  })
}

export function onGoToToolClicked(args: { title: string; url: string }) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'click_outbound_link',
    link_text: args.title,
    link_url: args.url,
    location: 'body',
  })
}

export function onEducationalVideosViewAllClicked(args: { title: string; url: string }) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'click_cta',
    link_text: args.title,
    link_url: args.url,
    location: 'body',
  })
}

export function onReferralProgramExpanded(args: { title: string }) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'click_dropdown',
    link_text: args.title,
    link_url: undefined,
    location: 'body',
  })
}

export function onEconomicCalendarFilterApplied(args: { title: string; url?: string }) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'click_filter',
    link_text: args.title,
    link_url: args.url ?? undefined,
    location: 'body',
  })
}

export function onToolHowItWorksClicked(args: { title: string; url?: string }) {
  window.dataLayer?.push({
    event: 'custom_event',
    event_name: 'open_popup',
    link_text: args.title,
    link_url: args.url ?? undefined,
    location: 'body',
  })
}
